import moment from "moment";
import React, { type CSSProperties, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, Col, Container, Input, Row, Spinner } from "reactstrap";

import KpiTile from "../../../../components/admin/KpiTile";
import PageHeader from "../../../../components/admin/PageHeader";
import PaymentDetailsModal from "../../../../components/charges/PaymentDetailsModal";
import RetryPaymentButton from "../../../../components/charges/RetryPaymentButton";
import {
  type FilterKey,
  formatMoney,
  formatUnpaid,
  groupByUser,
  loadAllUnpaidCharges,
  type UnpaidCharge,
  type UnpaidStatus,
} from "../../../../components/charges/unpaidCharges";
import useFetch from "../../../../hooks/useFetch";
import type { RootState } from "../../../../redux/reducers/session/state";

// ============================================================================
// Component
// ============================================================================

const MissingPayments: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.missingPayments" });
  const { t: tCharges } = useTranslation("charges");
  const navigate = useNavigate();
  const sessionUser = useSelector(({ sessionState }: RootState) => sessionState.user);

  // Filters live in the URL so Overview and other pages can deep-link into a view
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const filter = (searchParams.get("filter") as FilterKey | null) ?? "all";
  const reasonFilter = searchParams.get("reason");
  const updateParams = (patch: Record<string, string | null>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || v === "all") next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true }
    );
  const setSearch = (q: string) => updateParams({ q });
  const setFilter = (f: FilterKey) => updateParams({ filter: f });
  const setReasonFilter = (r: string | null) => updateParams({ reason: r });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selectedCharge, setSelectedCharge] = useState<UnpaidCharge | null>(null);

  const {
    data: charges,
    isLoading,
    isRefreshing,
    refresh,
  } = useFetch(loadAllUnpaidCharges, { cacheKey: "missing_payments_charges" });

  const groups = useMemo(() => groupByUser(charges ?? []), [charges]);

  // --- Aggregates -----------------------------------------------------------
  const totals = useMemo(() => {
    const unpaidEur = groups.reduce((s, g) => s + g.unpaidEur, 0);
    const failed = groups.reduce((s, g) => s + g.failedCount, 0);
    const escalated = groups.filter((g) => g.isEscalated || g.needsAttention).length;
    return { users: groups.length, unpaidEur, failed, escalated };
  }, [groups]);

  const reasonStats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const g of groups) for (const c of g.charges) if (c.reasonCode) counts[c.reasonCode] = (counts[c.reasonCode] ?? 0) + 1;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [groups]);

  // --- Filtering ------------------------------------------------------------
  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (q && !g.name.toLowerCase().includes(q) && !g.email.toLowerCase().includes(q)) return false;
      if (reasonFilter && !g.charges.some((c) => c.reasonCode === reasonFilter)) return false;
      switch (filter) {
        case "failed":
        case "pending":
        case "processing":
          return g.charges.some((c) => c.status === filter);
        case "escalated":
          return g.isEscalated || g.needsAttention;
        case "noPaymentMethod":
          return !g.hasValidCard || g.isCardExpired;
        default:
          return true;
      }
    });
  }, [groups, search, filter, reasonFilter]);

  const reasonLabel = (code: string | null): string => {
    if (!code) return t("noReason");
    const translated = tCharges(`paymentDetails.declineReasons.admin.${code}`, { defaultValue: "" });
    return String(translated) || code;
  };

  const statusLabel = (status: string) => String(tCharges(`chargesTableCard.statuses.${status}`, { defaultValue: status }));

  const toggleExpanded = (key: string) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  const filterButtons: { key: FilterKey; label: string; count: number; color: string }[] = [
    { key: "all", label: t("filters.all"), count: groups.length, color: "default" },
    { key: "failed", label: t("filters.failed"), count: groups.filter((g) => g.failedCount > 0).length, color: "danger" },
    {
      key: "pending",
      label: t("filters.pending"),
      count: groups.filter((g) => g.charges.some((c) => c.status === "pending")).length,
      color: "secondary",
    },
    {
      key: "processing",
      label: t("filters.processing"),
      count: groups.filter((g) => g.charges.some((c) => c.status === "processing")).length,
      color: "info",
    },
    { key: "escalated", label: t("filters.escalated"), count: totals.escalated, color: "warning" },
    {
      key: "noPaymentMethod",
      label: t("filters.noPaymentMethod"),
      count: groups.filter((g) => !g.hasValidCard || g.isCardExpired).length,
      color: "dark",
    },
  ];

  return (
    <>
      <PaymentDetailsModal
        isOpen={!!selectedCharge}
        toggle={() => setSelectedCharge(null)}
        charge={selectedCharge}
        user={sessionUser}
        onRefundSuccess={() => refresh()}
      />

      <Container className="mt-4" fluid>
        <PageHeader title={t("title")} subtitle={t("subtitle")} />
        <Row className="mb-3" style={{ rowGap: 12 }}>
          <Col xs={12} sm={6} xl={3}>
            <KpiTile
              label={t("stats.users")}
              value={totals.users}
              detail={t("stats.usersDescription")}
              icon="fas fa-user-clock"
              isLoading={isLoading}
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <KpiTile
              label={t("stats.amount")}
              value={`${totals.unpaidEur.toFixed(2)} €`}
              tone={totals.unpaidEur > 0 ? "crit" : "ok"}
              detail={t("stats.amountDescription")}
              icon="fas fa-euro-sign"
              isLoading={isLoading}
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <KpiTile
              label={t("stats.failed")}
              value={totals.failed}
              tone={totals.failed > 0 ? "warn" : "neutral"}
              detail={t("stats.failedDescription")}
              icon="fas fa-exclamation-triangle"
              isLoading={isLoading}
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <KpiTile
              label={t("stats.escalated")}
              value={totals.escalated}
              tone={totals.escalated > 0 ? "crit" : "ok"}
              detail={t("stats.escalatedDescription")}
              icon="fas fa-user-shield"
              isLoading={isLoading}
            />
          </Col>
        </Row>
        <Card>
          {/* Toolbar */}
          <CardBody style={styles.toolbar}>
            <div style={styles.toolbarTitle}>
              <h3 className="mb-0">{t("listTitle")}</h3>
            </div>
            <div style={styles.toolbarActions}>
              <Input
                bsSize="sm"
                placeholder={t("search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 260 }}
              />
              <Button size="sm" color="secondary" onClick={() => refresh()} disabled={isLoading || isRefreshing}>
                {isRefreshing ? <Spinner size="sm" /> : <i className="fas fa-sync-alt" />}
                <span className="ml-2">{t("refresh")}</span>
              </Button>
            </div>
          </CardBody>

          {/* Status filter chips */}
          <div style={styles.chipsRow}>
            {filterButtons.map(({ key, label, count, color }) => {
              const active = filter === key;
              return (
                <Button
                  key={key}
                  size="sm"
                  color={color}
                  outline={!active}
                  className="mb-2"
                  onClick={() => setFilter(key)}
                  style={styles.chip}
                >
                  {label}
                  <Badge color={active ? "light" : color} pill className="ml-2" style={styles.chipCount}>
                    {count}
                  </Badge>
                </Button>
              );
            })}
          </div>

          {/* Reason chips */}
          {reasonStats.length > 0 && (
            <div style={styles.reasonsRow}>
              <span style={styles.reasonsLabel}>{t("reasons")}</span>
              {reasonStats.map(([code, count]) => {
                const active = reasonFilter === code;
                return (
                  <button
                    key={code}
                    type="button"
                    title={reasonLabel(code)}
                    onClick={() => setReasonFilter(active ? null : code)}
                    style={{ ...styles.reasonChip, ...(active ? styles.reasonChipActive : {}) }}
                  >
                    <code style={styles.reasonCode}>{code}</code>
                    <span style={styles.reasonCount}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Body */}
          {isLoading ? (
            <div style={styles.loading}>
              <Spinner color="primary" />
            </div>
          ) : visibleGroups.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>
                <i className={groups.length === 0 ? "fas fa-check-circle" : "fas fa-filter"} />
              </div>
              <div style={styles.emptyText}>{groups.length === 0 ? t("empty") : t("emptyFiltered")}</div>
            </div>
          ) : (
            <div style={styles.list}>
              {/* Column headings */}
              <div style={{ ...styles.row, ...styles.headRow }}>
                <div style={{ ...styles.cellUser, ...styles.headCell }}>{t("columns.user")}</div>
                <div style={{ ...styles.cellAmount, ...styles.headCell }}>{t("columns.unpaid")}</div>
                <div style={{ ...styles.cellReason, ...styles.headCell }}>{t("columns.reason")}</div>
                <div style={{ ...styles.cellCard, ...styles.headCell }}>{t("columns.card")}</div>
                <div style={{ ...styles.cellSub, ...styles.headCell }}>{t("columns.subscription")}</div>
                <div style={{ ...styles.cellDate, ...styles.headCell }}>{t("columns.lastFailure")}</div>
                <div style={styles.cellActions} />
              </div>

              {visibleGroups.map((g) => {
                const key = String(g.userId);
                const isOpen = !!expanded[key];
                const card = g.subscription?.pm_paymentMethod?.pm_details;
                const subStatus = g.subscription?.pm_status ?? null;
                const subColor =
                  subStatus === "failed_escalated"
                    ? "#111827"
                    : subStatus === "unpaid" || subStatus === "failed"
                      ? "#ef4444"
                      : subStatus === "active"
                        ? "#10b981"
                        : "#9ca3af";
                const accent = g.isEscalated || g.needsAttention ? "#111827" : g.failedCount > 0 ? "#ef4444" : "#f59e0b";

                return (
                  <div key={key} style={{ ...styles.group, borderLeftColor: accent }}>
                    <div style={styles.row} onClick={() => toggleExpanded(key)} role="button">
                      {/* User */}
                      <div style={styles.cellUser}>
                        <div style={styles.userName}>
                          {g.name}
                          {(g.isEscalated || g.needsAttention) && (
                            <span style={styles.attentionBadge} title={t("adminAttention")}>
                              <i className="fas fa-exclamation" /> {t("adminAttention")}
                            </span>
                          )}
                        </div>
                        <div style={styles.userEmail}>{g.email}</div>
                        <div style={styles.userMeta}>
                          {t("chargesCount", { count: g.charges.length })}
                          {g.attempts > 0 && <> · {g.attempts} {t("columns.attempts").toLowerCase()}</>}
                        </div>
                      </div>

                      {/* Amount */}
                      <div style={styles.cellAmount}>
                        <div style={styles.amountValue}>{formatUnpaid(g.unpaidByCurrency)}</div>
                        <div style={styles.statusDots}>
                          {g.charges.map((c) => (
                            <span
                              key={String(c.charge.id)}
                              title={`${statusLabel(c.status)} · ${formatMoney(c.amount, c.currency)}`}
                              style={{ ...styles.statusDot, backgroundColor: STATUS_COLORS[c.status] }}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Reason */}
                      <div style={styles.cellReason}>
                        {g.latestReasonCode ? (
                          <>
                            <div style={styles.reasonText}>{reasonLabel(g.latestReasonCode)}</div>
                            <div style={styles.reasonMeta}>
                              <code style={styles.reasonCode}>{g.latestReasonCode}</code>
                              {g.latestBankMessage && <span style={styles.bankMessage}>{g.latestBankMessage}</span>}
                            </div>
                          </>
                        ) : (
                          <span style={styles.muted}>
                            {g.charges.some((c) => c.isManual) ? t("manualPayment") : t("noReason")}
                          </span>
                        )}
                      </div>

                      {/* Card */}
                      <div style={styles.cellCard}>
                        {card?.last4 ? (
                          <>
                            <div style={styles.cardLine}>
                              <i className="fas fa-credit-card mr-1" style={{ color: "#9ca3af" }} />
                              {(card.brand || "").toUpperCase()} •••• {card.last4}
                            </div>
                            <div style={styles.cardMeta}>
                              {card.exp_month}/{card.exp_year}
                              {g.isCardExpired ? (
                                <span style={{ ...styles.tag, ...styles.tagDanger }}>{t("cardExpired")}</span>
                              ) : g.hasValidCard ? (
                                <span style={{ ...styles.tag, ...styles.tagSuccess }}>{t("cardValid")}</span>
                              ) : (
                                <span style={{ ...styles.tag, ...styles.tagDanger }}>{t("cardInvalid")}</span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span style={{ ...styles.tag, ...styles.tagDanger }}>{t("noCard")}</span>
                        )}
                      </div>

                      {/* Subscription */}
                      <div style={styles.cellSub}>
                        {g.subscription ? (
                          <>
                            <div style={styles.subLine}>
                              <span style={{ ...styles.subDot, backgroundColor: subColor }} />
                              {t(`subscriptionStatuses.${subStatus}`, { defaultValue: subStatus || "" })}
                              {g.subscription.strategy?.name && (
                                <span style={styles.muted}> · {g.subscription.strategy.name}</span>
                              )}
                            </div>
                            <div style={styles.cardMeta}>
                              {(g.subscription.pm_failureCount ?? 0) > 0 && (
                                <span>{t("retries", { count: g.subscription.pm_failureCount ?? 0 })}</span>
                              )}
                              {g.subscription.pm_lastRetryAt && (
                                <span>
                                  {" · "}
                                  {t("lastRetry", { date: moment(g.subscription.pm_lastRetryAt).format("DD MMM") })}
                                </span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span style={styles.muted}>{t("noSubscription")}</span>
                        )}
                      </div>

                      {/* Last failure */}
                      <div style={styles.cellDate}>
                        {g.lastFailureAt ? (
                          <>
                            <div>{moment(g.lastFailureAt).format("DD MMM YYYY")}</div>
                            <div style={styles.muted}>{moment(g.lastFailureAt).fromNow()}</div>
                          </>
                        ) : (
                          <span style={styles.muted}>—</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={styles.cellActions}>
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          title={t("viewUser")}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            navigate(`/users/${g.userId}`);
                          }}
                        >
                          <i className="fas fa-user" />
                        </Button>
                        <Button
                          size="sm"
                          color="secondary"
                          title={isOpen ? t("hideCharges") : t("showCharges")}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            toggleExpanded(key);
                          }}
                        >
                          <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded charges */}
                    {isOpen && (
                      <div style={styles.chargesBox}>
                        {g.subscription?.id && (
                          <div style={styles.retryRow}>
                            <RetryPaymentButton
                              subscriptionId={g.subscription.id}
                              variant="admin"
                              onRetried={() => refresh()}
                            />
                          </div>
                        )}
                        <table style={styles.chargesTable}>
                          <thead>
                            <tr>
                              <th style={styles.th}>{t("chargeColumns.invoice")}</th>
                              <th style={styles.th}>{t("chargeColumns.period")}</th>
                              <th style={styles.th}>{t("chargeColumns.amount")}</th>
                              <th style={styles.th}>{t("chargeColumns.status")}</th>
                              <th style={styles.th}>{t("chargeColumns.reason")}</th>
                              <th style={styles.th}>{t("chargeColumns.attempts")}</th>
                              <th style={styles.th}>{t("chargeColumns.lastAttempt")}</th>
                              <th style={styles.th} />
                            </tr>
                          </thead>
                          <tbody>
                            {g.charges.map((c) => (
                              <tr key={String(c.charge.id)} style={styles.tr}>
                                <td style={styles.td}>
                                  <strong>#{String(c.charge.pm_invoiceNumber ?? c.charge.id)}</strong>
                                  {c.charge.bot_config?.exchange_account?.name && (
                                    <div style={styles.muted}>{c.charge.bot_config.exchange_account.name}</div>
                                  )}
                                </td>
                                <td style={styles.td}>
                                  {moment(c.charge.pm_periodStart).format("DD/MM/YY")} –{" "}
                                  {moment(c.charge.pm_periodEnd).format("DD/MM/YY")}
                                </td>
                                <td style={{ ...styles.td, fontWeight: 600 }}>{formatMoney(c.amount, c.currency)}</td>
                                <td style={styles.td}>
                                  <span style={{ ...styles.statusPill, backgroundColor: STATUS_COLORS[c.status] }}>
                                    {statusLabel(c.status)}
                                  </span>
                                  {c.isManual && <span style={{ ...styles.tag, ...styles.tagInfo }}>{t("manualPayment")}</span>}
                                </td>
                                <td style={styles.td}>
                                  {c.reasonCode ? (
                                    <>
                                      <div>{reasonLabel(c.reasonCode)}</div>
                                      <div style={styles.reasonMeta}>
                                        <code style={styles.reasonCode}>{c.reasonCode}</code>
                                        {c.bankMessage && <span style={styles.bankMessage}>{c.bankMessage}</span>}
                                      </div>
                                    </>
                                  ) : (
                                    <span style={styles.muted}>{t("noReason")}</span>
                                  )}
                                </td>
                                <td style={styles.td}>{c.attempts}</td>
                                <td style={styles.td}>
                                  {c.lastAttemptAt ? moment(c.lastAttemptAt).format("DD MMM YYYY, HH:mm") : "—"}
                                </td>
                                <td style={{ ...styles.td, textAlign: "right" }}>
                                  <Button
                                    size="sm"
                                    color={c.status === "failed" ? "danger" : "info"}
                                    outline
                                    title={t("viewDetails")}
                                    onClick={() => setSelectedCharge(c.charge)}
                                  >
                                    <i className={c.status === "failed" ? "fas fa-exclamation-triangle" : "fas fa-info-circle"} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </Container>
    </>
  );
};

// ============================================================================
// Styles
// ============================================================================

const STATUS_COLORS: Record<UnpaidStatus, string> = {
  failed: "#ef4444",
  pending: "#6b7280",
  processing: "#3b82f6",
};

const cellBase: CSSProperties = { padding: "14px 12px", minWidth: 0, fontSize: 13, color: "#374151" };

const styles: Record<string, CSSProperties> = {
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    borderBottom: "1px solid #e5e7eb",
  },
  toolbarTitle: { display: "flex", flexDirection: "column", minWidth: 0 },
  toolbarActions: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  chipsRow: { display: "flex", flexWrap: "wrap", gap: 8, padding: "14px 20px 4px" },
  chip: { borderRadius: 999, paddingLeft: 14, paddingRight: 10 },
  chipCount: { fontSize: 11, fontWeight: 600 },
  reasonsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
    padding: "4px 20px 12px",
    borderBottom: "1px solid #e5e7eb",
  },
  reasonsLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ca3af", marginRight: 4 },
  reasonChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    borderRadius: 999,
    padding: "2px 8px 2px 4px",
    cursor: "pointer",
    color: "#374151",
  },
  reasonChipActive: { borderColor: "#ef4444", background: "#fef2f2" },
  reasonCode: {
    fontSize: 11,
    fontFamily: "monospace",
    backgroundColor: "#e5e7eb",
    padding: "1px 6px",
    borderRadius: 4,
    color: "#4b5563",
  },
  reasonCount: { fontSize: 11, fontWeight: 700, color: "#6b7280" },
  loading: { display: "flex", justifyContent: "center", padding: 60 },
  empty: { display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: 12 },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#ecfdf5",
    color: "#10b981",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
  },
  emptyText: { color: "#6b7280", fontSize: 14 },
  list: { overflowX: "auto", maxHeight: "75vh", overflowY: "auto" },
  headRow: {
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    cursor: "default",
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
  headCell: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#9ca3af",
    fontWeight: 600,
    padding: "10px 12px",
  },
  group: {
    borderBottom: "1px solid #e5e7eb",
    borderLeft: "4px solid transparent",
    // Gives the sticky first cell something opaque to inherit.
    background: "#ffffff",
  },
  row: { display: "flex", alignItems: "center", cursor: "pointer", minWidth: 1100 },
  // Frozen so the row's identity stays visible while the wide row is scrolled.
  cellUser: {
    ...cellBase,
    flex: "2 1 220px",
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "inherit",
  },
  cellAmount: { ...cellBase, flex: "1 1 140px" },
  cellReason: { ...cellBase, flex: "2.5 1 260px" },
  cellCard: { ...cellBase, flex: "1.3 1 150px" },
  cellSub: { ...cellBase, flex: "1.5 1 170px" },
  cellDate: { ...cellBase, flex: "1 1 120px" },
  cellActions: { ...cellBase, flex: "0 0 100px", display: "flex", gap: 6, justifyContent: "flex-end" },
  userName: { fontWeight: 600, color: "#111827", fontSize: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  userEmail: { color: "#6b7280", fontSize: 12 },
  userMeta: { color: "#9ca3af", fontSize: 11, marginTop: 2 },
  attentionBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#fff",
    background: "#111827",
    borderRadius: 4,
    padding: "2px 6px",
  },
  amountValue: { fontWeight: 700, color: "#ef4444", fontSize: 15 },
  statusDots: { display: "flex", gap: 3, marginTop: 4, flexWrap: "wrap" },
  statusDot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
  reasonText: { color: "#991b1b", fontSize: 13 },
  reasonMeta: { display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" },
  bankMessage: { fontSize: 11, color: "#7f1d1d", fontFamily: "monospace" },
  cardLine: { fontSize: 13, color: "#111827" },
  cardMeta: { fontSize: 11, color: "#6b7280", marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  subLine: { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#111827" },
  subDot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  tag: { fontSize: 10, fontWeight: 600, borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap" },
  tagDanger: { background: "#fee2e2", color: "#991b1b" },
  tagSuccess: { background: "#d1fae5", color: "#065f46" },
  tagInfo: { background: "#dbeafe", color: "#1e40af", marginLeft: 6 },
  muted: { color: "#9ca3af", fontSize: 12 },
  retryRow: { display: "flex", justifyContent: "flex-end", padding: "4px 0 8px" },
  chargesBox: {
    background: "#f9fafb",
    borderTop: "1px dashed #e5e7eb",
    padding: "8px 16px 12px 24px",
    overflowX: "auto",
  },
  chargesTable: { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  th: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#9ca3af",
    fontWeight: 600,
    padding: "8px 10px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
  },
  tr: { borderBottom: "1px solid #eef0f3" },
  td: { padding: "10px", fontSize: 13, color: "#374151", verticalAlign: "top" },
  statusPill: {
    display: "inline-block",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 999,
    padding: "2px 10px",
  },
};

export default MissingPayments;
